import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  register as registerUser,
  clearError,
  RegisterCredentials,
} from "../../features/auth/authSlice";
import "./register.css";

// Form için tip tanımlaması - Yup şemasıyla uyumlu olmalı
interface RegisterFormInputs {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
  companyType: "single" | "agency";
}

// Form için validation şeması
const schema = yup.object().shape({
  username: yup
    .string()
    .required("Username is required")
    .min(3, "Username must be at least 3 characters"),
  email: yup
    .string()
    .required("Email is required")
    .email("Must be a valid email"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain uppercase, lowercase, number and special character"
    ),
  confirmPassword: yup
    .string()
    .required("Please confirm your password")
    .oneOf([yup.ref("password")], "Passwords must match"),
  terms: yup
    .boolean()
    .required("You must accept the terms and conditions")
    .oneOf([true], "You must accept the terms and conditions"),
  companyType: yup
    .string()
    .required("Please select company type")
    .oneOf(["single", "agency"], "Please select a valid company type"),
});

const RegisterPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error } = useAppSelector(
    (state) => state.auth
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // React Hook Form - tip uyumsuzluğunu çözmek için as any eklendi
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormInputs>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      terms: false,
      companyType: "single",
    },
  });

  // Kullanıcı zaten giriş yapmışsa dashboard'a yönlendir
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }

    // Component unmount olduğunda hata mesajını temizle
    return () => {
      dispatch(clearError());
    };
  }, [isAuthenticated, navigate, dispatch]);

  // Form gönderildiğinde
  const onSubmit = (data: RegisterFormInputs) => {
    const { username, email, password, companyType } = data;
    const role = companyType === "agency" ? "agency_owner" : "company_owner";
    dispatch(registerUser({ username, email, password, role }));
  };

  // Şifreyi göster/gizle
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Şifre onayını göster/gizle
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Rastgele partiküller oluşturmak için
  const renderParticles = () => {
    const particles = [];
    for (let i = 0; i < 30; i++) {
      particles.push(<div key={i} className="particle"></div>);
    }
    return particles;
  };

  // İzlenen companyType değeri
  const selectedCompanyType = watch("companyType");

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center register-container">
      {/* Animasyonlu arka plan */}
      <div className="background-animation">{renderParticles()}</div>

      <div className="register-card">
        <div className="register-header">
          <div className="register-logo">
            <div className="logo-circle"></div>
            <div className="logo-line"></div>
          </div>
          <h1>Create Account</h1>
          <p className="text-muted">Join us and get started</p>
        </div>

        {/* Hata mesajı */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {/* Register formu */}
        <form className="register-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <div className="input-group">
              <input
                type="text"
                className={`form-control ${errors.username ? "is-invalid" : ""}`}
                id="username"
                placeholder="Username"
                {...register("username")}
              />
              {errors.username && (
                <div className="invalid-feedback">
                  {errors.username.message}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <div className="input-group">
              <input
                type="email"
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                id="email"
                placeholder="Email address"
                {...register("email")}
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email.message}</div>
              )}
            </div>
          </div>

          <div className="form-group">
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                className={`form-control ${errors.password ? "is-invalid" : ""}`}
                id="password"
                placeholder="Password"
                {...register("password")}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
              >
                <i
                  className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}
                ></i>
              </button>
              {errors.password && (
                <div className="invalid-feedback">
                  {errors.password.message}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <div className="input-group">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                id="confirmPassword"
                placeholder="Confirm password"
                {...register("confirmPassword")}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={toggleConfirmPasswordVisibility}
              >
                <i
                  className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"}`}
                ></i>
              </button>
              {errors.confirmPassword && (
                <div className="invalid-feedback">
                  {errors.confirmPassword.message}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Select Company Type</label>
            <div className="role-selection">
              <div className="role-option">
                <input
                  type="radio"
                  id="company_type_agency"
                  value="agency"
                  className="role-radio"
                  {...register("companyType")}
                />
                <label
                  htmlFor="company_type_agency"
                  className={`role-label ${selectedCompanyType === "agency" ? "selected" : ""}`}
                >
                  <i className="bi bi-building role-icon"></i>
                  <div className="role-text">
                    <div className="role-title">Agency</div>
                    <div className="role-description">
                      Manage your agency and clients
                    </div>
                  </div>
                </label>
              </div>
              <div className="role-option">
                <input
                  type="radio"
                  id="company_type_single"
                  value="single"
                  className="role-radio"
                  {...register("companyType")}
                />
                <label
                  htmlFor="company_type_single"
                  className={`role-label ${selectedCompanyType === "single" ? "selected" : ""}`}
                >
                  <i className="bi bi-person-badge role-icon"></i>
                  <div className="role-text">
                    <div className="role-title">Single</div>
                    <div className="role-description">
                      Create a single company for your business
                    </div>
                  </div>
                </label>
              </div>
            </div>
            {errors.companyType && (
              <div className="invalid-feedback d-block">
                {errors.companyType.message}
              </div>
            )}
          </div>

          <div className="form-group terms-group">
            <div className="form-check">
              <input
                type="checkbox"
                className={`form-check-input ${errors.terms ? "is-invalid" : ""}`}
                id="terms"
                {...register("terms")}
              />
              <label className="form-check-label" htmlFor="terms">
                I agree to the{" "}
                <Link to="/terms" className="terms-link">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="terms-link">
                  Privacy Policy
                </Link>
              </label>
              {errors.terms && (
                <div className="invalid-feedback">{errors.terms.message}</div>
              )}
            </div>
          </div>

          <button
            type="submit"
            className={`btn btn-primary w-100 ${isLoading ? "loading" : ""}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </button>

          <div className="text-center mt-4">
            <Link to="/login" className="login-link">
              Already have an account? <span>Sign in</span>
            </Link>
          </div>
        </form>

        <div className="text-center mt-4 copyright">
          <p className="mb-0">
            &copy; {new Date().getFullYear()} AI Digital Marketing Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
